import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Alert, Card, EmptyState, Spinner, StatusPill, formatSlot } from '../../components/ui';

export default function NannyDashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([api.get('/api/nanny/profile'), api.get('/api/nanny/bookings')])
      .then(([profile, booked]) => {
        setData(profile);
        setBookings(booked);
      })
      .catch((err) => setError(err.message));
  }, []);

  if (error) return <Alert kind="error">{error}</Alert>;
  if (!data) return <Spinner />;

  const { nanny, checklist } = data;
  const { applicationStatus, applicationStatusLabel, availabilityStatus, availabilityStatusLabel, adminMessage } =
    nanny.profile;
  const outstanding = checklist.filter((item) => !item.done);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('nannyDashboard.welcomeBack', { name: nanny.firstName })}</h1>
          <p className="muted">{t('nannyDashboard.subtitle')}</p>
        </div>
        <div className="page-head-pills">
          <StatusPill status={applicationStatus} label={applicationStatusLabel} />
          {applicationStatus === 'approved' && (
            <StatusPill status={availabilityStatus} label={availabilityStatusLabel} />
          )}
        </div>
      </div>

      <Card title={t('nannyDashboard.applicationStatus')}>
        <p>{t(`nannyDashboard.statusMessage.${applicationStatus}`)}</p>
        {adminMessage && (
          <Alert kind={applicationStatus === 'rejected' ? 'error' : 'info'}>
            <strong>{t('nannyDashboard.messageFromTeam')} </strong>
            {adminMessage}
          </Alert>
        )}

        {applicationStatus !== 'approved' && (
          <>
            <ul className="checklist">
              {checklist.map((item) => (
                <li key={item.key} className={item.done ? 'done' : ''}>
                  <span aria-hidden="true">{item.done ? '✓' : '○'}</span>
                  {t(`nannyDashboard.checklist.${item.key}`)}
                </li>
              ))}
            </ul>
            {outstanding.length > 0 && (
              <Link className="btn btn-primary" to="/nanny/application">
                {t('nannyDashboard.continueApplication')}
              </Link>
            )}
          </>
        )}
      </Card>

      <Card
        title={t('nannyDashboard.upcomingBookings')}
        actions={<Link className="btn btn-ghost" to="/nanny/bookings">{t('nannyDashboard.viewAll')}</Link>}
      >
        {bookings?.upcoming?.length ? (
          <ul className="booking-list">
            {bookings.upcoming.slice(0, 4).map((booking) => (
              <li key={booking.id}>
                <div>
                  <strong>{formatSlot(booking)}</strong>
                  <small className="muted">
                    {booking.family.familyName || booking.family.name} · {booking.family.suburb || booking.family.city || '—'}
                  </small>
                </div>
                <StatusPill status={booking.status} label={booking.statusLabel} />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title={t('nannyDashboard.noBookingsYet')}>
            {applicationStatus === 'approved'
              ? t('nannyDashboard.noBookingsApproved')
              : t('nannyDashboard.noBookingsNotApproved')}
          </EmptyState>
        )}
      </Card>
    </>
  );
}
